import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

/**
 * Hook to check if current time is within user's active hours
 * Returns whether water logging should be allowed
 */
export function useActiveHours() {
  const [isWithinActiveHours, setIsWithinActiveHours] = useState(true);
  const [isActiveDay, setIsActiveDay] = useState(true);

  // Fetch user profile to get active hours settings
  const { data: profile } = useQuery<any>({
    queryKey: ["/api/profile"],
    retry: false,
  });

  useEffect(() => {
    if (!profile) {
      // Default to allowing if profile not loaded yet
      setIsWithinActiveHours(true);
      setIsActiveDay(true);
      return;
    }

    const checkActiveStatus = () => {
      const now = new Date();
      
      // Check active day
      const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const today = daysOfWeek[now.getDay()];
      const activeDays = profile.activeDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const isDayActive = activeDays.includes(today);
      setIsActiveDay(isDayActive);

      if (!isDayActive) {
        setIsWithinActiveHours(false);
        return;
      }

      // Check active hours
      const activeHoursStart = profile.activeHoursStart || '09:00';
      const activeHoursEnd = profile.activeHoursEnd || '18:00';

      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentMinutes = currentHour * 60 + currentMinute;

      const [startHour, startMin] = activeHoursStart.split(':').map(Number);
      const [endHour, endMin] = activeHoursEnd.split(':').map(Number);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      const isWithinHours = currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      setIsWithinActiveHours(isWithinHours);
    };

    // Check immediately
    checkActiveStatus();

    // Check every minute
    const interval = setInterval(checkActiveStatus, 60000);

    return () => clearInterval(interval);
  }, [profile]);

  return {
    isWithinActiveHours: isWithinActiveHours && isActiveDay,
    isActiveDay,
    activeHoursStart: profile?.activeHoursStart || '09:00',
    activeHoursEnd: profile?.activeHoursEnd || '18:00',
    activeDays: profile?.activeDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  };
}

